import { type FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { DateRangeSelector } from '@features/filters';
import {
  DecisionSelector,
  PrioritySelector,
  ScanAndCriticalResultsSelector,
  StatusDateRangeSelector,
  StatusSelector,
  TagsSelect,
} from '@features/marketplace';

import {
  initialMarketplaceSectionFilters,
  initialMerchantsFilters,
  initialMerchantsSort,
  type RequestForm,
} from '@entities/merchant';

import { AnalyticsEvent } from '@shared/analytics';
import { Flex, IconButton, Icons, Text } from '@shared/ui-kit';

import { isMarketplacesFiltersActive } from '../../lib';

interface Props {
  isLoading: boolean;
}

const MarketplaceSection: FC<Props> = (props) => {
  const { isLoading = false } = props;

  const t = useTranslations();

  const { control, reset } = useFormContext<RequestForm>();
  const formValues = useWatch({ control });
  const { filter } = formValues;

  const hasFilters = isMarketplacesFiltersActive(
    initialMerchantsFilters,
    filter,
  );

  const resetFilters = () => {
    reset({
      ...formValues,
      filter: {
        ...filter,
        ...initialMarketplaceSectionFilters,
      },
      sort: {
        ...formValues.sort,
        key: initialMerchantsSort.key,
      },
    });
  };

  return (
    <Flex
      direction="column"
      rowGap="4"
    >
      <Flex
        alignItems="center"
        columnGap="4"
      >
        <Text
          fontSize="sm"
          as="b"
        >
          {t('Marketplace.title')}
        </Text>
        <IconButton
          data-testid="reset-filters"
          aria-label={t('Filters.resetButtonAriaLabel')}
          icon={
            <Icons.ClearFilters
              w="5"
              h="5"
            />
          }
          variant="ghost"
          size="xs"
          m="0"
          isDisabled={isLoading}
          visibility={hasFilters ? 'visible' : 'hidden'}
          transition="all .2s linear"
          onClick={resetFilters}
        />
      </Flex>
      <PrioritySelector
        name="filter.marketplace_priorities"
        control={control}
      />
      <Flex
        alignItems="flex-start"
        columnGap="9"
      >
        <DecisionSelector
          name="filter.marketplace_decisions"
          control={control}
          isMerchantsPage
        />
        <StatusSelector
          name="filter.marketplace_statuses"
          control={control}
          isMerchantsPage
        />
      </Flex>
      <ScanAndCriticalResultsSelector isMerchantsPage />
      <Flex gap="9">
        <DateRangeSelector
          analyticsEvent={AnalyticsEvent.ImportDateFilter}
          name="filter.marketplace_last_imported_at"
          label={t('Marketplace.Filters.DateRangeSelector.import')}
          control={control}
        />
        <StatusDateRangeSelector
          name="filter.marketplace_status_changed_at"
          control={control}
          statuses={filter?.marketplace_statuses ?? []}
        />
      </Flex>
      <TagsSelect
        name="filter.marketplace_tags"
        control={control}
      />
    </Flex>
  );
};

export default MarketplaceSection;
